import React, { Component } from 'react'
import { rem } from 'polished'

import TextArea from 'components/Common/Form/Input/TextArea'
import TextButton from 'LuxKit/Button/TextButton'
import styled from 'styled-components'
import { formToObject } from 'lib/forms/formToObject'
import TextInput from 'components/Common/Form/Input/TextInput'
import Caption from 'LuxKit/Typography/Caption'
import { without } from 'lib/array/arrayUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import InputChip from 'LuxKit/Chips/InputChip'
import { ShareConfig } from './ShareModal'

const Emails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
`

const SubmitButton = styled(TextButton)`
  align-self: flex-end;
`

interface Props {
  shareMessage: string;
  onShare: (type: string) => void;
  closeShareModal: () => void;
  shareConfig: ShareConfig;
  hasShareMessage?: boolean;
}

interface State {
  friendsEmailList: Array<string>;
  trySubmitMessage: string;
  processing: boolean;
  processed?: boolean;
}

class ShareWithFriendForm extends Component<React.PropsWithChildren<Props>, State> {
  state: State = {
    friendsEmailList: [],
    trySubmitMessage: '',
    processing: false,
  }

  static defaultProps = {
    hasShareMessage: true,
  }

  onSubmitSuccess = () => {
    this.props.onShare('email form')
    this.setState({
      trySubmitMessage: 'Thanks for sharing!',
      processing: false,
      processed: true,
    })
    setTimeout(this.props.closeShareModal, 3000)
  }

  onSubmitFailure = (e: any, optMessage?: string) => {
    let message = optMessage || 'Oops something went wrong, please try again'
    if (e && e.status === 429) {
      message = 'You have reached the limit for sending emails, please try again tomorrow.'
    }
    this.setState({
      trySubmitMessage: message,
      processing: false,
      processed: true,
    })
  }

  handleEmailsResponse = (emailsReponse: Array<any>) => {
    const erroredEmails = emailsReponse.filter(el => el.error)

    if (erroredEmails.length === 0) {
      this.onSubmitSuccess()
    } else {
      const limitReachedEmails = erroredEmails.filter(el => el.error.status === 429)

      // if there is an error 429 (limit reached) - show limit reached message (even though some emails may have been sent before reaching the limit)
      if (limitReachedEmails.length > 0) {
        this.onSubmitFailure(limitReachedEmails[0])
      } else {
        const invalidEmails = erroredEmails.reduce((acc, cur) => {
          if (cur.error?.message?.includes('This email seems to be invalid')) {
            const invalidEmail = cur.error.message.split('This email seems to be invalid: ')[1]
            acc.push(invalidEmail)
          }
          return acc
        }, [])

        // limit is not reached, no invalid emails, so all other errors will be just passed on
        if (invalidEmails.length === 0) {
          this.onSubmitFailure(erroredEmails[0])
          return
        }

        const invalidEmailsMessage = (invalidEmails.length === 1) ?
          `This email seems to be invalid: ${invalidEmails}` :
          `These emails seems to be invalid: ${invalidEmails.join(', ')}`

        this.setState({ friendsEmailList: [] })
        this.onSubmitFailure(null, invalidEmailsMessage)
      }
    }
  }

  handleSubmit = async(event: React.FormEvent<HTMLFormElement>) => {
    const { shareConfig } = this.props
    event.preventDefault()
    this.setState({
      trySubmitMessage: '',
      processing: true,
      processed: false,
    })

    if (!this.state.friendsEmailList.length) {
      this.setState({
        trySubmitMessage: 'Email is not valid or empty',
        processing: false,
      })
      return false
    }

    const formValues = formToObject<{ friendMessage: string }>(event.currentTarget)

    const promises = this.state.friendsEmailList.map(email => (
      shareConfig.onSend?.({
        friend_email: email,
        message: formValues.friendMessage,
      }).catch(err => ({ error: err }))
    ))

    const emailsReponse = await Promise.all(promises)

    this.handleEmailsResponse(emailsReponse)
  }

  onEmailSubmit = (input: HTMLInputElement) => {
    if (input.validity.valid && input.value) {
      const email = input.value
      this.setState((prevState) => ({
        friendsEmailList: [...prevState.friendsEmailList, email],
      }))
      input.value = ''
    }
  }

  onEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    this.onEmailSubmit(e.currentTarget)
  }

  onEmailKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key.toLowerCase() === 'enter') {
      e.preventDefault()
      this.onEmailSubmit(e.currentTarget)
    }
  }

  onRemoveEmail = (e: React.MouseEvent<HTMLButtonElement>) => {
    const email = e.currentTarget.dataset.email

    if (email) {
      this.setState((prevState) => ({
        friendsEmailList: without(prevState.friendsEmailList, email),
      }))
    }
  }

  render() {
    const {
      shareMessage,
    } = this.props

    const {
      processing,
      trySubmitMessage,
      friendsEmailList,
    } = this.state

    return (
      <form onSubmit={this.handleSubmit}>
        <VerticalSpacer gap={8}>
          <div>
            <TextInput
              type="email"
              label="Email addresses"
              onKeyDown={this.onEmailKeydown}
              onBlur={this.onEmailBlur}
              optionalIndicatorEnabled={false}
            />
            <Emails>
              {friendsEmailList.map(email => <InputChip
                key={email}
                data-email={email}
                onClick={this.onRemoveEmail}
                variant="filled"
              >
                {email}
              </InputChip>)}
            </Emails>
            {friendsEmailList.length >= 20 && <Caption variant="large" colour="critical">
              You can only send a maximum of 20 emails per day.
            </Caption>}
          </div>
          {!!shareMessage &&
            <TextArea
              label="Personal message:"
              id="friendMessage"
              rows={4}
              required
              type="text"
              name="friendMessage"
              placeholder="Message"
              defaultValue={shareMessage}
            />
          }
          {trySubmitMessage && <CaptionTextBlock
            variant="large"
            weight="bold"
            startIcon={<LineExclamationCircleIcon colour="critical" />}
          >
            {trySubmitMessage}
          </CaptionTextBlock>}
          <SubmitButton
            kind="primary"
            fit="mobile-full-width"
            disabled={processing}
            type="submit"
          >
            {processing ? 'Sending' : 'Send'}
          </SubmitButton>
        </VerticalSpacer>
      </form>
    )
  }
}

export default ShareWithFriendForm
